import { ModalCancelMeetingComponent } from '@base/components/private/modals/modal-cancel-meeting/modal-cancel-meeting.component';
import { TypeOfOpenProfile, TypeOfScheduleMeeting } from '@base/enums/widgets.enum';
import { JudyLoader } from '@base/models/responses/judy.res';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { WidgetSrvAbstract } from '@base/abstracts/services/widget.abstract';
import { EventChatWidgetKeys } from '@base/enums/widgets.enum';
import { GlobalState } from '@base/models/others/global-state';
import { ChatMessage, EventChatWidget } from '@base/models/others/widgets';
import { DynamicImportLoaderService } from '@base/services/private/dynamic-import.service';
import { ModalService } from '@base/services/private/modal.service';
import { extractDynamicImport, getMfeAttributes, removeScript } from '@base/utils';
import { SupportedLanguages } from '@config/supported-languages.enum';
import { WhiteLabel } from '@config/whitelabel-keys.constant';
import { environment } from 'environments/environment';
import { lastValueFrom, take } from 'rxjs';
import { GlobalStateService } from '@base/services/private/global-state.service';
import { MeetService } from '@base/services/private/meet.service';
import { IdParams } from '@base/models/requests/requests.req';
import { ExhibitorDetailComponent } from '@pages/meet/exhibitor-list/exhibitor-detail/exhibitor-detail.component';
import { PeopleDetailComponent } from '@pages/meet/people-list/people-detail/people-detail.component';
import { ModalRequestMeetComponent } from '@base/components/modal-request-meet/modal-request-meet.component';
import { CookieService } from 'ngx-cookie-service';
import { ModalConfig } from '@base/models/others/modal-config';
import { ModalPeopleListComponent } from '@base/components/private/modals/modal-people-list/modal-people-list.component';
import { Attendee } from '@base/models/responses/attendee.res';
import { ModalNamecardComponent } from '@base/components/modal-namecard/modal-namecard.component';
import { defaultModalConfig } from '@base/constants/common';
import { ModalCreateVideoMeetingComponent } from '@base/components/private/modals/modal-create-video-meeting/modal-create-video-meeting.component';
import { ModalCancelVideoMeetingComponent } from '@base/components/private/modals/modal-cancel-video-meeting/modal-cancel-video-meeting.component';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { ModalInformationComponent } from '@base/components/modal-information/modal-information.component';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { TypeOfRequestLabel } from '@base/enums/request-label.enum';
import { JubliaAIMFEResponse } from '@base/models/responses/jublia-ai.res';
import { EventService } from '@base/services/private/event.service';
import { JubliaAIRedirection } from '@base/constants/menu-config';
import { Router } from '@angular/router';
import { SessionService } from '@base/services/private/session.service';
import { JubliaAiService } from '@base/services/private/jublia-ai.service';
import { LoadMFESrvAbstract } from '@base/abstracts/services/load-mfe.abstract';
import { FaroService } from '@core/services/faro.service';
import { JUBLIA_AI_CHATROOM_ID } from '@base/enums/jublia-ai.enum';
import { resolveChatProvider } from '@base/models/chat-provider';

/**
 * Whitelist of `chatroom_type` values accepted from the chat widget for the
 * SEND_VCARD CTA. Anything outside this set is forwarded as `undefined` so the
 * downstream vcard payload stays well-typed.
 */
const CHATROOM_TYPES = [0, 1, 'MEETING', 'COMPANY'] as const;
export type ChatroomType = (typeof CHATROOM_TYPES)[number];

function isChatroomType(value: unknown): value is ChatroomType {
  return (CHATROOM_TYPES as readonly unknown[]).includes(value);
}

@Component({
  selector: 'app-chat',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ModalHeaderComponent, TranslatePipe],
  providers: [TranslatePipe],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss',
})
export class ChatComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('jubliaContainer', { static: false }) jubliaContainer!: ElementRef;

  jubliaChatElement: HTMLElement | null = null;
  private readonly globalStateSrv = inject(GlobalStateService);
  private readonly dynamicImportLoaderSrv = inject(DynamicImportLoaderService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly modalSrv = inject(ModalService);
  private readonly meetSrv = inject(MeetService);
  private readonly sessionSrv = inject(SessionService);
  private readonly jubliaAiSrv = inject(JubliaAiService);

  private chatSrv!: WidgetSrvAbstract;
  private readonly translatePipe = inject(TranslatePipe);
  private readonly cookieSrv = inject(CookieService);
  private readonly router = inject(Router);
  private readonly eventSrv = inject(EventService);
  private readonly faroSrv = inject(FaroService);
  private loadMfeSrv: LoadMFESrvAbstract | null = null;

  readonly isLoading = signal(true);
  readonly loadError = signal(false);

  @Input() modalId = '';
  @Input() chatroomId = '';
  @Input() peopleId = '';
  @Input() action?: TypeOfRequestLabel | undefined;
  @Input() searchId = '';
  @Input() askAnythingMessage = '';

  cookieValue = this.cookieSrv.get('custom-session');
  globalState?: GlobalState | null = null;
  chatLoading = `${environment.jubliaAsset}/general/image/chat-loading.gif`;
  judyLoader: JudyLoader | null = null;
  SupportedLanguages = SupportedLanguages;
  attendees: Attendee[] | [] = [];
  modalRequestMeet: Type<unknown> | undefined;

  constructor() {
    if (typeof customElements !== 'undefined' && !customElements.get('jublia-chat-module')) {
      console.warn('JubliaChatModule not defined. Make sure its script is loaded globally.');
    }
  }

  ngOnInit(): void {
    (async () => {
      await this.getGlobalState();
    })();

    setTimeout(() => {
      this.modalSrv.closeAll(); // close all if any modal opened before chat to handle schedule meeting from chat if the status meeting is unstructured
    }, 1000);
  }

  ngAfterViewInit(): void {
    (async () => {
      await this.loadComponents();
    })();
  }

  /**
   * Asynchronously loads components for the explore page.
   *
   * @returns Promise<void> Resolves when all components are successfully loaded.
   */
  async loadComponents(): Promise<void> {
    try {
      const components = await Promise.allSettled([
        this.dynamicImportLoaderSrv.loadComponent(this.viewContainerRef, WhiteLabel.COMPONENTS.ModalRequestMeet, false),
      ]);

      if (components) {
        const [modalRequestMeet] = components;

        this.modalRequestMeet = extractDynamicImport(modalRequestMeet)?.component || undefined;
      } else {
        throw new Error('Components could not be loaded.');
      }
    } catch (error) {
      console.error('Failed to load components:', error);
    }
  }

  ngOnDestroy(): void {
    this.destroyChatModule();
  }

  /**
   * Requests and initializes a chatroom for the given peopleId.
   *
   * @returns void
   */
  getRequestChatroom(): void {
    const params: IdParams = {
      id: this.peopleId,
    };
    this.meetSrv.getRequestChatroom(params).subscribe({
      next: (res) => {
        this.chatroomId = res.chatroom_id;
        this.initChatroomId();
        this.getJudyLoader();
      },
      error: (err) => {
        console.error('Error fetching chatroom:', err);
        this.loadError.set(true);
        this.isLoading.set(false);
      },
    });
  }

  /**
   * Fetches the global state asynchronously.
   *
   * @returns Promise that resolves after state is fetched.
   */
  async getGlobalState(): Promise<void> {
    try {
      this.globalState = await lastValueFrom(this.globalStateSrv.getState$());
      await this.loadServices();
      await this.loadWidget();
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  }

  async loadWidget(): Promise<boolean> {
    try {
      await this.loadMfeSrv?.loadChat();
      if (!customElements.get('jublia-chat-module')) {
        throw new Error('jublia-chat-module failed to register');
      }
      return true;
    } catch (error) {
      console.error('Error loading chat MFE:', error);
      this.loadError.set(true);
      this.isLoading.set(false);
      return false;
    }
  }

  retryLoad(): void {
    if (this.jubliaChatElement && this.jubliaContainer?.nativeElement?.contains(this.jubliaChatElement)) {
      this.jubliaContainer.nativeElement.removeChild(this.jubliaChatElement);
    }
    this.jubliaChatElement = null;
    this.loadError.set(false);
    this.isLoading.set(true);
    void this.loadWidget().then((loaded) => {
      if (loaded) this.getJudyLoader();
    });
  }

  /**
   * Asynchronously loads services needed for the component.
   *
   * Initializes required services and ensures they are ready for use.
   *
   * @returns Promise<void> Resolves when all services are successfully loaded.
   */
  private async loadServices(): Promise<void> {
    try {
      const loadedService = await Promise.all([
        this.dynamicImportLoaderSrv.loadService(WhiteLabel.SERVICES.Chat),
        this.dynamicImportLoaderSrv.loadService(WhiteLabel.SERVICES.LoadMfe),
      ]);
      if (loadedService) {
        const [chatSrv, loadMfeSrv] = loadedService;
        this.chatSrv = chatSrv as WidgetSrvAbstract;
        this.loadMfeSrv = loadMfeSrv as LoadMFESrvAbstract;

        if (this.peopleId) {
          this.getRequestChatroom();
        } else {
          this.getJudyLoader();
        }
      } else {
        throw new Error('Service could not be loaded.');
      }
    } catch (error) {
      console.error('Failed to load Services:', error);
    }
  }

  /**
   * Destroys the Jublia chat module by removing its script and DOM element.
   *
   * @returns void
   */
  destroyChatModule(): void {
    removeScript(environment.jubliaChat);
    if (this.jubliaChatElement && this.jubliaContainer?.nativeElement.contains(this.jubliaChatElement)) {
      const attrs = getMfeAttributes(this.jubliaChatElement, ['auth-token']);
      this.jubliaContainer?.nativeElement.removeChild(this.jubliaChatElement);
      this.faroSrv.pushMfeDestroy('jublia-chat-module', attrs);
    }
    this.jubliaChatElement = null;
    this.globalStateSrv.doRefetch(true, 'CHAT_REINITIALIZE');
  }

  /**
   * Initializes the chatroom ID from global state if not already set.
   *
   * @returns void
   */
  initChatroomId(): void {
    if (this.chatroomId) return;

    const chatroomId = this.globalState?.loader?.chat_id;
    if (chatroomId && (chatroomId !== null || chatroomId !== 'room')) {
      if (chatroomId === 'heyjudy') {
        this.chatroomId = JUBLIA_AI_CHATROOM_ID;
      } else {
        this.chatroomId = chatroomId as string;
      }
    } else {
      this.chatroomId = '';
    }
  }

  /**
   * Retrieves and processes data related to the Judy loader.
   *
   * @returns void
   */
  getJudyLoader(): void {
    this.chatSrv?.loader().subscribe({
      next: (res) => {
        if (!res) {
          this.loadError.set(true);
          this.isLoading.set(false);
          return;
        }

        this.judyLoader = res;
        this.judyLoader.jcuid = this.cookieValue;
        this.initChatroomId();

        setTimeout(() => {
          this.instanceJubliaChat();
        }, 500);
      },
      error: (err) => {
        console.error('Error fetching Judy loader:', err);
        this.loadError.set(true);
        this.isLoading.set(false);
      },
    });
  }

  /**
   * Creates and initializes the Jublia chat module element,
   * sets attributes, and appends it to the container.
   *
   * @returns void
   */
  instanceJubliaChat(): void {
    this.jubliaChatElement = document.createElement('jublia-chat-module');

    Object.assign(this.jubliaChatElement, { faroInstance: this.faroSrv.getInstance() });
    if (this.globalState?.validate && this.globalState.loader) {
      const { event_background, person_lang } = this.globalState.validate;
      const { event_hash, event_color } = this.globalState.loader;
      const provider = resolveChatProvider(this.globalState.loader.feature_flag?.chat_provider);
      if (!provider) {
        console.error('Cannot instantiate chat: unsupported provider');
        this.jubliaChatElement = null;
        this.loadError.set(true);
        this.isLoading.set(false);
        return;
      }
      const isAnon = this.globalStateSrv.isAnon();
      const localLang = this.sessionSrv.getItem('lang') as string;

      this.jubliaChatElement.setAttribute('event-hash', event_hash);
      this.jubliaChatElement.setAttribute('chat-provider', provider);
      this.jubliaChatElement.setAttribute('event-color', event_color);
      this.jubliaChatElement.setAttribute('event-background', event_background);
      this.jubliaChatElement.setAttribute('lang', (isAnon ? localLang : person_lang) || SupportedLanguages.EN);
      this.jubliaChatElement.setAttribute('platform', 'WEB');
      this.jubliaChatElement.setAttribute('state', 'CHAT');
      this.jubliaChatElement.setAttribute('is-angular-framework', 'true');
      if (!this.globalStateSrv.isAnon() && this.judyLoader?.jcuid) {
        this.jubliaChatElement.setAttribute('auth-token', this.judyLoader?.jcuid);
      }
      if (this.chatroomId && this.chatroomId !== 'room') {
        this.jubliaChatElement.setAttribute('chatroom-id', this.chatroomId);
      }
      if (this.searchId) {
        this.jubliaChatElement.setAttribute('search-id', this.searchId);
      }
      if (this.askAnythingMessage) {
        this.jubliaChatElement.setAttribute('ask-anything-message', this.askAnythingMessage);
      }
      if (this.loadMfeSrv?.customBcpChat) {
        this.jubliaChatElement.setAttribute('is-bcp', this.loadMfeSrv?.customBcpChat);
      }
      if (this.loadMfeSrv?.hideJubliaAi) {
        this.jubliaChatElement.setAttribute('hide-jublia-ai', 'ENABLE');
      }
      this.jubliaChatElement.addEventListener('chatListener', this.listenerChatModule.bind(this) as EventListener);

      setTimeout(() => this.applyChatThemeOverrides(), 500);
    }
    this.jubliaContainer?.nativeElement?.appendChild(this.jubliaChatElement);

    const attrs = getMfeAttributes(this.jubliaChatElement, ['auth-token']);
    console.info('JubliaChatModule instance added to DOM via wrapper.');
    this.faroSrv.pushMfeInit('jublia-chat-module', attrs);
    this.isLoading.set(false);
  }

  /**
   * Injects Web theme colors into the Jublia chat module's shadow DOM.
   * @returns void
   */
  applyChatThemeOverrides(): void {
    const shadow = this.jubliaChatElement?.shadowRoot;

    if (shadow) {
      const style = document.createElement('style');
      style.textContent = `
      app-chat-detail-component, .flex-fill, .icon-base, .participant-container:hover, .judy-policy, .ri-arrow-left-line, .text-body-tertiary:hover, .text-color-contrast {color: var(--bg-color-contrast) !important;}
      `;
      shadow.appendChild(style);
    }
  }

  /**
   * Event listener for 'chatListener' events on the Jublia chat module.
   *
   * @param event - The custom event containing chat widget details.
   * @returns void
   */
  listenerChatModule(event: CustomEvent<EventChatWidget>) {
    if (event.detail) {
      const { action, callback, message } = event.detail;

      if (action === 'CTA') {
        this.handleCTAListerner(message, callback);
      }

      if (action === 'VOID') {
        this.handleVoidListener(message, callback);
      }
    }
  }

  showModalInformation(message: string): void {
    const modalId = 'modal-information';
    const isExternalMode = this.globalStateSrv.isExternalMode();
    const data: Partial<ModalInformationComponent> = {
      modalId,
      title: isExternalMode ? '' : this.translatePipe.transform('HOLD_ON'),
      message,
      description: isExternalMode ? this.translatePipe.transform('CLOSE_WINDOW') : '',
    };

    const config: ModalConfig = {
      ...defaultModalConfig,
      size: 'md',
      modalId,
    };

    this.modalSrv.open(ModalInformationComponent, data, config);
  }

  handleVoidListener(message: ChatMessage, callback: () => void): void {
    const { key, value } = message;

    switch (key) {
      case EventChatWidgetKeys.ERROR_MESSAGE: {
        // `typeof null === 'object'`, so a null payload would otherwise reach
        // the bracket access on the right-hand side and throw a TypeError.
        const fallbackMessage = this.translatePipe.transform('SOMETHING_WENT_WRONG');
        const error = value !== null && typeof value === 'object' ? value['error'] : null;
        const message = typeof error === 'string' && error.trim() ? error : fallbackMessage;
        this.showModalInformation(message);
        if (callback) {
          callback();
        }
        break;
      }
      case EventChatWidgetKeys.UNREAD_COUNT:
        this.globalStateSrv.setHasNewChat(typeof value === 'number' && value > 0);
        break;
      default:
        console.warn(`Unhandled VOID key: ${key}`);
        break;
    }
  }

  /**
   * Closes the current modal and optionally executes a callback.
   *
   * @param callback - Optional function to execute after closing.
   * @returns void
   */
  close(callback?: () => void): void {
    this.modalSrv.closeAll([]); // close all including chat modal
    this.destroyChatModule();
    if (callback) {
      callback();
    }
  }

  /**
   * Handles CTA events from the chat widget based on the given message.
   *
   * @param message - The chat message containing action details.
   * @param callback - Callback function to execute after handling the event.
   * @returns void
   */
  handleCTAListerner(message: ChatMessage, callback: () => void) {
    const { key, value } = message;
    // `typeof null === 'object'`, so a null payload would otherwise fall
    // through and the destructuring below would throw a TypeError.
    if (value === null || typeof value !== 'object') {
      console.warn(`Invalid CTA payload for key: ${key}`);
      return;
    }
    const { object_id, exhibitor_id, type, tab: _tab, chatroom_type } = value;

    switch (key) {
      case EventChatWidgetKeys.CLOSE_CHAT:
        this.close(callback);
        this.destroyChatModule();
        break;
      case EventChatWidgetKeys.SCHEDULE_MEETING: {
        if (type === TypeOfScheduleMeeting.COMPANY) {
          this.getCompanyAttendeeDetail(object_id, callback);
        } else if (type === TypeOfScheduleMeeting.PEOPLE) {
          this.handleSchedulePeople(object_id, callback, false);
        }
        break;
      }

      case EventChatWidgetKeys.RESCEDULE:
        this.handleReschedulePeople(object_id, callback);
        break;

      case EventChatWidgetKeys.OPEN_PROFILE:
        if (type === TypeOfOpenProfile.COMPANY) {
          this.handleModalExhibitorDetail(object_id, type, callback);
        } else {
          this.handleModalPeopleDetail(object_id, type, callback);
        }
        break;
      case EventChatWidgetKeys.CANCEL_MEETING:
        this.handleCancelMeeting(object_id, callback);
        break;

      case EventChatWidgetKeys.SEND_VCARD:
        if (this.jubliaChatElement) {
          const data = {
            object_id: object_id,
            exhibitor_id: exhibitor_id,
            chatroom_type: isChatroomType(chatroom_type) ? chatroom_type : undefined,
          };
          this.showModalNameCard(data);
        }
        break;

      case EventChatWidgetKeys.CREATE_VIDEO_MEETING:
        if (this.jubliaChatElement) {
          const data = {
            object_id: object_id,
            exhibitor_id: exhibitor_id,
            utcInfo: value['utc_info'] as string,
            matchAppEndDate: value['match_app_end'] as string,
          };
          this.showModalCreateVideoMeeting(data);
        }
        break;

      case EventChatWidgetKeys.CANCEL_VIDEO_MEETING:
        if (this.jubliaChatElement) {
          const data = {
            object_id: object_id,
            exhibitor_id: exhibitor_id,
          };
          this.showModalCancelVideoMeeting(data);
        }
        break;

      case EventChatWidgetKeys.JUBLIA_AI_CTA:
        this.handleRedirectionJubliaAI(value as unknown as JubliaAIMFEResponse);
        break;

      default:
        console.warn(`Unhandled CTA key: ${key}`);
        break;
    }
  }

  /**
   * Redirection handle for chat widget
   */
  handleRedirectionJubliaAI(result: JubliaAIMFEResponse): void {
    try {
      const isAvailable = this.jubliaAiSrv.checkEntityAvailability(result.result.target);
      if (!isAvailable) {
        this.close();
        this.showModalInformation(this.translatePipe.transform('CANNOT_ACCESS_THIS_FEATURE'));
        return;
      }
      const eventDir = this.eventSrv?.data?.name;
      const isBrandedDomain = this.eventSrv?.data?.isBrandedDomain;
      const eventDirPath = isBrandedDomain ? '/' : eventDir;
      const navigate = JubliaAIRedirection(eventDirPath, result.result.target || [eventDirPath]);
      this.router
        .navigate(navigate, {
          state: { searchId: result?.search_id || '', target: result?.result?.target || '' },
          onSameUrlNavigation: 'reload',
        })
        .then(() => {
          this.close();
        });
    } catch (error) {
      console.error('Error in handleRedirectionJubliaAI:', error);
    }
  }

  /**
   * Opens the modal to cancel a video meeting.
   *
   * @param object_id - The ID of the object.
   * @param exhibitor_id - Optional ID of the exhibitor.
   * @returns void
   */
  showModalCancelVideoMeeting({ object_id, exhibitor_id }: { object_id: string; exhibitor_id?: string }): void {
    const modalId = 'modal-cancel-video-meeting';
    const data: Partial<ModalCancelVideoMeetingComponent> = {
      modalId,
    };
    const config: ModalConfig = {
      ...defaultModalConfig,
      backdrop: 'static',
      keyboard: false,
      size: 'md',
      modalId,
    };

    const modalRef = this.modalSrv.open(ModalCancelVideoMeetingComponent, data, config);
    modalRef.closed.subscribe({
      next: (res: { key: string; value: Record<string, unknown> }) => {
        if (res?.key === 'CANCEL_VIDEO_MEETING' && res?.value) {
          if (this.jubliaChatElement) {
            const payload = {
              object_id: object_id,
              exhibitor_id: exhibitor_id,
              ...res.value,
            };
            this.jubliaChatElement.setAttribute('video-meeting-payload', JSON.stringify(payload));

            setTimeout(() => {
              this.jubliaChatElement?.setAttribute('video-meeting-payload', '');
              this.jubliaChatElement?.removeAttribute('video-meeting-payload');
            }, 700); // Clean up attribute after sending
          }
        }
      },
      error: (err) => {
        console.error('Error in create video meeting modal:', err);
      },
    });
  }

  /**
   * Opens the modal to create a video meeting.
   * @param param - object containing object_id and optional exhibitor_id
   */
  showModalCreateVideoMeeting({
    object_id,
    exhibitor_id,
    utcInfo,
    matchAppEndDate,
  }: {
    object_id: string;
    exhibitor_id?: string;
    utcInfo: string;
    matchAppEndDate: string;
  }): void {
    const modalId = 'modal-create-video-meeting';
    const data: Partial<ModalCreateVideoMeetingComponent> = {
      modalId,
      utcInfo,
      matchAppEndDate,
    };
    const config: ModalConfig = {
      ...defaultModalConfig,
      backdrop: 'static',
      keyboard: false,
      size: 'md',
      modalId,
    };

    const modalRef = this.modalSrv.open(ModalCreateVideoMeetingComponent, data, config);
    modalRef.closed.subscribe({
      next: (res: { key: string; value: Record<string, unknown> }) => {
        if (res?.key === 'CREATE_VIDEO_MEETING' && res?.value) {
          if (this.jubliaChatElement) {
            const payload = {
              object_id: object_id,
              exhibitor_id: exhibitor_id,
              ...res.value,
            };
            this.jubliaChatElement.setAttribute('video-meeting-payload', JSON.stringify(payload));

            setTimeout(() => {
              this.jubliaChatElement?.setAttribute('video-meeting-payload', '');
              this.jubliaChatElement?.removeAttribute('video-meeting-payload');
            }, 700); // Clean up attribute after sending
          }
        }
      },
      error: (err) => {
        console.error('Error in create video meeting modal:', err);
      },
    });
  }

  /**
   * Opens the name card modal for the current exhibitor.
   * @returns void
   */
  showModalNameCard({
    object_id,
    exhibitor_id,
    chatroom_type,
  }: {
    object_id: string;
    exhibitor_id?: string;
    chatroom_type?: ChatroomType;
  }): void {
    const data: Partial<ModalNamecardComponent> = {
      exhibitorId: exhibitor_id,
      isChatRoom: true,
    };
    const config: ModalConfig = {
      backdrop: 'static',
      keyboard: false,
    };
    const modalRef = this.modalSrv.showModalNameCard({
      addsData: data,
      addsConfig: config,
    });

    modalRef?.closed.subscribe({
      next: (res: { key: string; value: Record<string, unknown> }) => {
        if (res?.key === 'SEND_VCARD' && res?.value) {
          if (this.jubliaChatElement) {
            const payload = {
              object_id: object_id,
              exhibitor_id: exhibitor_id,
              ...res.value,
              chatroom_type,
            };
            this.jubliaChatElement.setAttribute('vcard-payload', JSON.stringify(payload));

            setTimeout(() => {
              this.jubliaChatElement?.setAttribute('vcard-payload', '');
              this.jubliaChatElement?.removeAttribute('vcard-payload');
            }, 700); // Clean up attribute after sending
          }
        }
      },
      error: (err) => {
        console.error('Error in name card modal:', err);
      },
    });
  }

  private openBusinessMatchingFromChat(
    id: string,
    action: TypeOfRequestLabel,
    callback?: () => void,
    isReschedule = false
  ): void {
    const modalRef =
      action === TypeOfRequestLabel.CANCEL_MEETING
        ? this.modalSrv.showModalCancelMeeting({ addsData: { peopleId: id, action } })
        : this.modalSrv.showModalRequestMeet({
            addsData: {
              peopleId: id,
              isReschedule,
              isMeetFromChat: true,
              action: isReschedule ? null : TypeOfRequestLabel.ACCEPT_MEETING,
            },
          });
    modalRef?.closed.pipe(take(1)).subscribe({ next: () => callback?.() });
  }

  /**
   * Opens a modal to schedule a meeting with a person.
   *
   * @param id - The ID of the person.
   * @param callback - Optional callback after scheduling.
   * @param isTypeCompany - Flag indicating if the type is company.
   * @returns void
   */
  handleSchedulePeople(id: string, callback?: () => void, isTypeCompany = false): void {
    if (this.globalStateSrv.isMatchmakingImprovements()) {
      this.openBusinessMatchingFromChat(id, TypeOfRequestLabel.REQUEST_MEETING, callback);
      return;
    }

    const data: Partial<ModalRequestMeetComponent> = {
      peopleId: id,
      isCloseAll: true,
      isMeetFromChat: true,
      isSkipCheckMeeting: isTypeCompany ? false : true,
    };
    const config: ModalConfig = {
      backdrop: 'static',
      keyboard: false,
    };
    const modalRef = this.modalSrv.showModalRequestMeet({
      addsData: data,
      addsConfig: config,
      component: this.modalRequestMeet,
    });
    modalRef?.closed.subscribe({
      next: () => {
        if (callback) {
          callback();
        }
      },
      error: (err) => {
        console.error('Error scheduling meeting with person:', err);
      },
    });
  }

  /**
   * Opens a modal to reschedule a meeting with a person.
   *
   * @param id - The ID of the person.
   * @param callback - Optional callback after rescheduling.
   * @returns void
   */
  async handleReschedulePeople(id: string, callback?: () => void): Promise<void> {
    if (this.loadMfeSrv?.customDisableMeetingFromSense) {
      const meetingSource = await this.loadMfeSrv.checkAdminInitiateMeeting(id);
      if (meetingSource === 'SENSE') {
        return;
      }
    }

    if (this.globalStateSrv.isMatchmakingImprovements()) {
      this.openBusinessMatchingFromChat(id, TypeOfRequestLabel.RESCHEDULE_MEETING, callback, true);
      return;
    }

    const data: Partial<ModalRequestMeetComponent> = {
      peopleId: id,
      isMeetFromChat: true,
      isCloseAll: true,
      isSkipCheckMeeting: true,
      isReschedule: true,
    };
    const config: ModalConfig = {
      backdrop: 'static',
      keyboard: false,
    };
    const modalRef = this.modalSrv.showModalRequestMeet({
      addsData: data,
      addsConfig: config,
      component: this.modalRequestMeet,
    });

    modalRef?.closed.subscribe({
      next: () => {
        if (callback) {
          callback();
        }
      },
      error: (err) => {
        console.error('Error rescheduling meeting with person:', err);
      },
    });
  }

  /**
   * Opens a modal to cancel a meeting.
   *
   * @param id - The ID of the person.
   * @returns void
   */
  async handleCancelMeeting(id: string, callback?: () => void): Promise<void> {
    if (this.loadMfeSrv?.customDisableMeetingFromSense) {
      const meetingSource = await this.loadMfeSrv.checkAdminInitiateMeeting(id);
      if (meetingSource === 'SENSE') {
        return;
      }
    }

    if (this.globalStateSrv.isMatchmakingImprovements()) {
      this.openBusinessMatchingFromChat(id, TypeOfRequestLabel.CANCEL_MEETING, callback);
      return;
    }

    const data: Partial<ModalCancelMeetingComponent> = {
      peopleId: id,
    };
    const modalRef = this.modalSrv.showModalCancelMeeting({
      addsData: data,
    });

    modalRef?.closed.subscribe({
      next: (res) => {
        if (res === 'close') return;
        if (callback) {
          callback();
        }
      },
      error: (err) => {
        console.error('Error canceling meeting:', err);
      },
    });
  }

  /**
   * Opens a modal to view a person's profile.
   *
   * @param id - The ID of the person.
   * @param type - The type of profile to open (default is ME).
   * @param callback - Optional callback after opening.
   * @returns void
   */
  handleModalPeopleDetail(id: string, _type: string = TypeOfOpenProfile.ME, callback?: () => void): void {
    const data: Partial<PeopleDetailComponent> = {
      peopleId: id,
    };
    const modalRef = this.modalSrv.showModalPeopleDetail({
      addsData: data,
    });

    modalRef?.closed.subscribe({
      next: () => {
        if (callback) {
          callback();
        }
      },
      error: (err) => {
        console.error('Error fetching people detail:', err);
      },
    });
  }

  /**
   * Opens a modal to view a company's profile.
   *
   * @param id - The ID of the company.
   * @param type - The type of profile to open (default is COMPANY).
   * @param callback - Optional callback after opening.
   * @returns void
   */
  handleModalExhibitorDetail(id: string, _type: string = TypeOfOpenProfile.COMPANY, callback?: () => void): void {
    const data: Partial<ExhibitorDetailComponent> = {
      exhibitorId: id,
      // callback: callback,
    };
    const modalRef = this.modalSrv.showModalExhibitorDetail({
      addsData: data,
    });
    modalRef?.closed.subscribe({
      next: () => {
        if (callback) {
          callback();
        }
      },
      error: (err) => {
        console.error('Error fetching exhibitor detail:', err);
      },
    });
  }

  /**
   * Opens the people list modal showing multiple exhibitor attendees.
   * @param callback - Optional callback after closing the modal.
   * @returns void
   */
  showModalPeopleList(callback?: () => void): void {
    const data: Partial<ModalPeopleListComponent> = {
      attendees: this.attendees,
    };
    const modalRef = this.modalSrv.showModalPeopleList({
      addsData: data,
    });

    modalRef?.closed.subscribe({
      next: () => {
        if (callback) {
          callback();
        }
      },
      error: (err) => {
        console.error('Error fetching people list:', err);
      },
    });
  }

  /**
   * Fetches detailed information about the exhibitor's attendees.
   * @param exhibitorId - The ID of the exhibitor.
   * @param callback - Optional callback after fetching details.
   * @returns void
   */
  getCompanyAttendeeDetail(exhibitorId: string, callback?: () => void): void {
    if (!exhibitorId) return;
    this.meetSrv.getCompanyDetail(exhibitorId).subscribe({
      next: (res) => {
        this.attendees = res.attendees;

        if (this.attendees.length === 1) {
          this.handleSchedulePeople(this.attendees[0].id_attendee, callback, true);
        } else if (this.attendees.length > 1) {
          this.showModalPeopleList(callback);
        } else {
          console.warn('No attendees found for this exhibitor.');
        }
      },
      error: (err) => {
        console.error('Error fetching company detail:', err);
      },
    });
  }
}
